import { forwardRef, useState } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export interface AvatarProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Diamètre du cercle en pixels. Défaut 26 (taille du brand book). */
  size?: number;
  /** Initiale(s) à centrer dans le cercle, et repli quand la photo manque ou ne charge pas. Ignoré si `children` est fourni. */
  initials?: string | undefined;
  /** Photo de la personne (URL). Prime sur les initiales, qui restent le repli. */
  src?: string | null | undefined;
  /** Contenu personnalisé (icône, image…) à la place des initiales. */
  children?: ReactNode;
}

/**
 * La photo à montrer : `src` tant qu'il n'a pas échoué à charger, sinon rien
 * (les initiales). Pure : un test la tient sans DOM.
 *
 * `enEchec` est l'URL qui a échoué, pas un booléen : une nouvelle URL (la
 * personne a changé de photo) retente sa chance sans remonter le composant.
 */
export function photoAffichable(
  src: string | null | undefined,
  enEchec: string | null,
): string | undefined {
  if (src == null || src.trim() === '') return undefined;
  return src === enEchec ? undefined : src;
}

/**
 * Avatar : cercle plein gris encre avec l'initiale centrée en blanc, ou la
 * photo de la personne si `src` est fourni (recadrée en `cover`). C'est LE
 * composant d'avatar d'une personne : tout écran qui montre quelqu'un passe par
 * lui, pour que la photo de son compte s'affiche partout pareil.
 *
 * - Photo absente, ou qui échoue à charger (lien mort, réseau coupé) : retour
 *   aux initiales, sans cercle vide ni icône d'image cassée.
 * - `referrerPolicy="no-referrer"` : les photos Google (`lh3.googleusercontent.com`)
 *   refusent de se charger quand la page leur envoie un référent.
 *
 * Brand book : 26px · border-radius:50% · bg ink-500 · color #FFFFFF · Inter 600.
 * La taille de police suit le diamètre (~0.42x) pour rester lisible à toutes les tailles.
 */
export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  { className, size = 26, initials, src, children, style, ...props },
  ref,
) {
  const [enEchec, setEnEchec] = useState<string | null>(null);
  const photo = photoAffichable(src, enEchec);
  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full bg-ink-500 font-semibold leading-none text-white',
        className,
      )}
      // size en px de reference (1920) : rendu en rem pour suivre l'echelle globale.
      style={{
        width: `${size / 16}rem`,
        height: `${size / 16}rem`,
        fontSize: `${Math.round(size * 0.42) / 16}rem`,
        ...style,
      }}
      {...props}
    >
      {children ??
        (photo !== undefined ? (
          <img
            src={photo}
            alt=""
            referrerPolicy="no-referrer"
            decoding="async"
            draggable={false}
            onError={() => setEnEchec(photo)}
            className="h-full w-full object-cover"
          />
        ) : (
          initials
        ))}
    </span>
  );
});
